"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { NextIntlClientProvider, type AbstractIntlMessages } from "next-intl";
import {
  defaultLocale,
  isValidLocale,
  localeCookieName,
  rtlLocales,
  type AppLocale,
} from "@/i18n/config";
import en from "@/messages/en.json";

const loadMessages: Record<
  AppLocale,
  () => Promise<AbstractIntlMessages>
> = {
  en: async () => en,
  de: () => import("@/messages/de.json").then((m) => m.default),
  zh: () => import("@/messages/zh.json").then((m) => m.default),
  hi: () => import("@/messages/hi.json").then((m) => m.default),
  ar: () => import("@/messages/ar.json").then((m) => m.default),
};

type LocaleContextValue = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  isPending: boolean;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readStoredLocale(): AppLocale {
  if (typeof document === "undefined") return defaultLocale;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${localeCookieName}=`));
  const value = match?.split("=")[1];
  return value && isValidLocale(value) ? value : defaultLocale;
}

function applyDocumentLocale(locale: AppLocale) {
  document.documentElement.lang = locale;
  document.documentElement.dir = rtlLocales.has(locale) ? "rtl" : "ltr";
}

export function AppLocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AppLocale>(defaultLocale);
  const [messages, setMessages] = useState<AbstractIntlMessages>(en);
  const [isPending, setIsPending] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const stored = readStoredLocale();
    setLocaleState(stored);
    applyDocumentLocale(stored);
    if (stored !== defaultLocale) {
      void loadMessages[stored]().then(setMessages);
    }
    setReady(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void loadMessages[locale]().then((next) => {
      if (!cancelled) setMessages(next);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setLocale = useCallback((next: AppLocale) => {
    if (!isValidLocale(next)) return;
    setIsPending(true);
    setLocaleState(next);
    document.cookie = `${localeCookieName}=${next}; path=/; max-age=31536000; SameSite=Lax`;
    applyDocumentLocale(next);
    queueMicrotask(() => setIsPending(false));
  }, []);

  const value = useMemo(
    () => ({ locale, setLocale, isPending }),
    [locale, setLocale, isPending],
  );

  return (
    <LocaleContext.Provider value={value}>
      <NextIntlClientProvider locale={locale} messages={messages} timeZone="UTC">
        <span className="sr-only" aria-live="polite">
          {ready ? locale : defaultLocale}
        </span>
        {children}
      </NextIntlClientProvider>
    </LocaleContext.Provider>
  );
}

export function useAppLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useAppLocale must be used within AppLocaleProvider");
  }
  return ctx;
}
